import React, { useState, useEffect } from 'react';
import { 
  getDistricts, 
  getNeighborhoods, 
  getStreets, 
  getApartments, 
  himRecordQuery 
} from '../services/addressService';

const HimQuery = () => {
  const [activeTab, setActiveTab] = useState('fullAddress');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  // Full address form state
  const [fullAddress, setFullAddress] = useState('');

  // Manual selection form state
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [streets, setStreets] = useState([]);
  const [apartments, setApartments] = useState([]);
  
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('');
  const [selectedStreet, setSelectedStreet] = useState('');
  const [selectedApartment, setSelectedApartment] = useState('');
  const [doorNumber, setDoorNumber] = useState('');

  // Load districts on component mount
  useEffect(() => {
    loadDistricts();
  }, []);

  const loadDistricts = async () => {
    try {
      const response = await getDistricts();
      if (response.status === 200) {
        setDistricts(response.data || []);
      } else {
        console.error('Unexpected districts response status:', response.status);
        setDistricts([]);
      }
    } catch (err) {
      console.error('Error loading districts:', err);
      setDistricts([]);
    }
  };

  const loadNeighborhoods = async (districtId) => {
    try {
      const response = await getNeighborhoods(districtId);
      if (response.status === 200) {
        setNeighborhoods(response.data || []);
      } else {
        console.error('Unexpected neighborhoods response status:', response.status);
        setNeighborhoods([]);
      }
      setStreets([]);
      setApartments([]);
      setSelectedNeighborhood('');
      setSelectedStreet('');
      setSelectedApartment('');
    } catch (err) {
      console.error('Error loading neighborhoods:', err);
      setNeighborhoods([]);
    }
  };

  const loadStreets = async (neighborhoodId) => {
    try {
      const response = await getStreets(neighborhoodId);
      if (response.status === 200) {
        setStreets(response.data || []);
      } else {
        console.error('Unexpected streets response status:', response.status);
        setStreets([]);
      }
      setApartments([]);
      setSelectedStreet('');
      setSelectedApartment('');
    } catch (err) {
      console.error('Error loading streets:', err);
      setStreets([]);
    }
  };

  const loadApartments = async (neighborhoodId, streetId) => {
    try {
      const response = await getApartments(neighborhoodId, streetId);
      if (response.status === 200) {
        setApartments(response.data || []);
      } else {
        console.error('Unexpected apartments response status:', response.status);
        setApartments([]);
      }
      setSelectedApartment('');
    } catch (err) {
      console.error('Error loading apartments:', err);
      setApartments([]);
    }
  };

  const handleDistrictChange = (e) => {
    const districtId = e.target.value;
    setSelectedDistrict(districtId);
    if (districtId) {
      loadNeighborhoods(districtId);
    }
  };

  const handleNeighborhoodChange = (e) => {
    const neighborhoodId = e.target.value;
    setSelectedNeighborhood(neighborhoodId);
    if (neighborhoodId) {
      loadStreets(neighborhoodId);
    }
  };

  const handleStreetChange = (e) => {
    const streetId = e.target.value;
    setSelectedStreet(streetId);
    if (streetId && selectedNeighborhood) {
      loadApartments(selectedNeighborhood, streetId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      let payload = {};

      if (activeTab === 'fullAddress') {
        if (!fullAddress.trim()) {
          setError('Lütfen tam adres giriniz');
          setLoading(false);
          return;
        }
        payload = {
          FullAdres: fullAddress.trim()
        };
      } else {
        if (!selectedDistrict || !selectedNeighborhood || !selectedStreet || !selectedApartment || !doorNumber.trim()) {
          setError('Lütfen tüm alanları doldurunuz');
          setLoading(false);
          return;
        }
        
        const selectedDistrictData = districts.find(d => d.id === selectedDistrict);
        const selectedNeighborhoodData = neighborhoods.find(n => n.id === selectedNeighborhood);
        const selectedStreetData = streets.find(s => s.id === selectedStreet);
        const selectedApartmentData = apartments.find(a => a.id === selectedApartment);

        payload = {
          Doornumber: doorNumber.trim(),
          Apartment: selectedApartmentData?.kapino || '',
          Neighborhood: selectedNeighborhoodData?.ad || '',
          District: selectedDistrictData?.ad || '',
          Street: selectedStreetData?.ad || '',
          FullAdres: ''
        };
      }

      const result = await himRecordQuery(payload);
      setResponse(result);
    } catch (err) {
      setError(err.message || 'Bir hata oluştu');
      console.error('HIM Record Query Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    if (activeTab === 'fullAddress') {
      setFullAddress('');
    } else {
      setSelectedDistrict('');
      setSelectedNeighborhood('');
      setSelectedStreet('');
      setSelectedApartment('');
      setDoorNumber('');
      setNeighborhoods([]);
      setStreets([]);
      setApartments([]);
    }
    setResponse(null);
    setError(null);
  };

  return (
    <div className="him-query">
      <div className="him-query__header">
        <h1 className="page-title">HIM Kayıt Sorgulama</h1>
        <p>Adres bilgilerini sorgulamak için aşağıdaki formu kullanabilirsiniz.</p>
      </div>

      <div className="him-query__tabs">
        <button
          className={`him-query__tab ${activeTab === 'fullAddress' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('fullAddress');
            resetForm();
          }}
        >
          Tam Adres Girişi
        </button>
        <button
          className={`him-query__tab ${activeTab === 'manual' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('manual');
            resetForm();
          }}
        >
          Adres Seçimi
        </button>
      </div>

      <form onSubmit={handleSubmit} className="him-query__form">
        {activeTab === 'fullAddress' ? (
          <div className="him-query__full-address">
            <p>Adresi kopyalayıp yapıştırabilirsiniz.</p>
            <br/>
            <p>MAHALLE SOKAK APARTMAN KAPI NO, İLÇE</p>
            <br/>
            <div className="form-group">
              <label htmlFor="fullAddress">Tam Adres *</label>
              <textarea
                id="fullAddress"
                value={fullAddress}
                onChange={(e) => setFullAddress(e.target.value)}
                placeholder="Tam adres bilgisini giriniz..."
                rows={4}
                required
              />
            </div>
          </div>
        ) : (
          <div className="him-query__manual-selection">
            <div className="form-group">
              <label htmlFor="district">İlçe *</label>
              <select
                id="district"
                value={selectedDistrict}
                onChange={handleDistrictChange}
                required
              >
                <option value="">İlçe seçiniz</option>
                {Array.isArray(districts) && districts.map((district) => (
                  <option key={district.id} value={district.id}>
                    {district.ad}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="neighborhood">Mahalle *</label>
              <select
                id="neighborhood"
                value={selectedNeighborhood}
                onChange={handleNeighborhoodChange}
                required
                disabled={!selectedDistrict}
              >
                <option value="">Mahalle seçiniz</option>
                {Array.isArray(neighborhoods) && neighborhoods.map((neighborhood) => (
                  <option key={neighborhood.id} value={neighborhood.id}>
                    {neighborhood.ad}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="street">Sokak *</label>
              <select
                id="street"
                value={selectedStreet}
                onChange={handleStreetChange}
                required
                disabled={!selectedNeighborhood}
              >
                <option value="">Sokak seçiniz</option>
                {Array.isArray(streets) && streets.map((street) => (
                  <option key={street.id} value={street.id}>
                    {street.ad}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="apartment">Apartman *</label>
              <select
                id="apartment"
                value={selectedApartment}
                onChange={(e) => setSelectedApartment(e.target.value)}
                required
                disabled={!selectedStreet}
              >
                <option value="">Apartman seçiniz</option>
                {Array.isArray(apartments) && apartments.map((apartment) => (
                  <option key={apartment.id} value={apartment.id}>
                    {apartment.kapino}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="doorNumber">Kapı No *</label>
              <input
                type="text"
                id="doorNumber"
                value={doorNumber}
                onChange={(e) => setDoorNumber(e.target.value)}
                placeholder="Kapı numarasını giriniz"
                required
                disabled={!selectedApartment}
              />
            </div>
          </div>
        )}

        <div className="him-query__actions">
          <button type="button" onClick={resetForm} className="btn btn-secondary">
            Temizle
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Sorgulanıyor...' : 'Sorgula'}
          </button>
        </div>
      </form>

      {error && (
        <div className="him-query__error">
          <p>{error}</p>
        </div>
      )}

      {response && response.data && response.data.length > 0 && (
        <div className="him-query__response">
          <h3>Sorgu Sonucu ({response.data.length} kayıt bulundu):</h3>
          <div className="him-query__cards">
            {response.data
              .sort((a, b) => {
                const dateA = a.posetVerme ? new Date(a.posetVerme) : new Date(a.ziyaretTarihi);
                const dateB = b.posetVerme ? new Date(b.posetVerme) : new Date(b.ziyaretTarihi);
                return dateB - dateA;
              })
              .map((record, index) => (
                <div key={index} className={`him-query__card ${(record.posetVerme && !record.atikAlma) ? 'active' : ''}`}>
                  <div className="him-query__card-header">
                    <h4>{record.fullAdres}</h4>
                    <span className="him-query__card-date">
                      {new Date(record.posetVerme || record.ziyaretTarihi).toLocaleDateString('tr-TR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  
                  <div className="him-query__card-content">
                    
                    {record.aciklama && (
                      <div className="him-query__card-row">
                        <span className="him-query__card-label">Açıklama:</span>
                        <span className="him-query__card-value">{record.aciklama}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="him-query__card-timeline">
                    <div className="him-query__timeline-item">
                      <span className="him-query__timeline-label">Poşet Verme:</span>
                      <span className="him-query__timeline-value">
                        {record.posetVerme ? new Date(record.posetVerme).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Belirtilmemiş'}
                      </span>
                    </div>
                    
                    <div className="him-query__timeline-item">
                      <span className="him-query__timeline-label">Atık Alma:</span>
                      <span className="him-query__timeline-value">
                        {record.atikAlma ? new Date(record.atikAlma).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Belirtilmemiş'}
                      </span>
                    </div>
                    
                    <div className="him-query__timeline-item">
                      <span className="him-query__timeline-label">Ziyaret Tarihi:</span>
                      <span className="him-query__timeline-value">
                        {record.ziyaretTarihi ? new Date(record.ziyaretTarihi).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Belirtilmemiş'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {response && response.data && response.data.length === 0 && (
        <div className="him-query__response">
          <h3>Sorgu Sonucu:</h3>
          <div className="him-query__no-results">
            <p>Bu adres için kayıt bulunamadı.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HimQuery;
